import React from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { mob } from "../SustainTeamManagementScreen.styles";
import { TeamMember, MOCK_TICKET_COUNTS, MEMBER_SPECIALTY, isWorkingToday } from "./SustainTeamTypes";
import { useTheme } from "../../../theme";

interface Props {
  teamData: TeamMember[];
  onNavigate: (screen: string) => void;
}

const MobileTeamView: React.FC<Props> = ({ teamData, onNavigate }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const maxTickets   = Math.max(...Object.values(MOCK_TICKET_COUNTS), 1);
  const activeTickets = Object.values(MOCK_TICKET_COUNTS).reduce((a, b) => a + b, 0);

  return (
    <View style={[mob.root, isDark && { backgroundColor: colors.background.page }]}>
      {/* Header */}
      <View style={[mob.header, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.divider }]}>
        <Pressable onPress={() => onNavigate("Ticketing Portal")} style={mob.backBtn} hitSlop={8}>
          <MaterialIcons name="arrow-back" size={22} color={isDark ? colors.text.primary : "#1a2332"} />
        </Pressable>
        <View>
          <Text style={[mob.headerTitle, isDark && { color: colors.text.primary }]}>Sustain Team</Text>
          <Text style={[mob.headerSub, isDark && { color: colors.text.secondary }]}>{teamData.length} members · {activeTickets} active tickets</Text>
        </View>
      </View>

      <ScrollView style={mob.scroll} contentContainerStyle={mob.scrollContent}>
        <Text style={[mob.sectionLabel, isDark && { color: colors.text.secondary }]}>TEAM MEMBERS</Text>

        {teamData.map((member) => {
          const onDuty       = member.workingStatus === "On Duty";
          const workingToday = isWorkingToday(member);
          const tickets      = MOCK_TICKET_COUNTS[member.id] ?? 0;
          const specialty    = MEMBER_SPECIALTY[member.id] ?? (member.role === "lead" ? "Lead" : "Member");

          // Green = on duty now, Yellow = working today but off-hours, Gray = not working today
          const dotColor    = onDuty ? "#22C55E" : workingToday ? "#FBBF24" : "#9CA3AF";
          const showLoadBar = (workingToday || onDuty) && tickets > 0;

          return (
            <View key={member.id} style={[mob.card, isDark && { backgroundColor: colors.background.surface }]}>
              <View style={[mob.avatar, { backgroundColor: member.avatarColor }]}>
                <Text style={mob.avatarText}>{member.initials}</Text>
              </View>
              <View style={mob.cardContent}>
                <View style={mob.nameRow}>
                  <Text style={[mob.name, isDark && { color: colors.text.primary }]}>{member.name}</Text>
                  <View style={[mob.statusDot, { backgroundColor: dotColor }]} />
                </View>
                <Text style={[mob.specialty, isDark && { color: colors.text.secondary }]}>{specialty}</Text>
                {showLoadBar ? (
                  <View style={mob.loadRow}>
                    <Text style={[mob.loadLabel, isDark && { color: colors.text.secondary }]}>Load:</Text>
                    <View style={[mob.loadBarBg, isDark && { backgroundColor: colors.border.default }]}>
                      <View style={[mob.loadBarFill, { width: `${Math.round((tickets / maxTickets) * 100)}%` as any }, isDark && { backgroundColor: colors.brand.primary }]} />
                    </View>
                    <Text style={[mob.loadCount, isDark && { color: colors.text.primary }]}>{tickets} tickets</Text>
                  </View>
                ) : (
                  <Text style={[mob.offHoursText, isDark && { color: colors.text.disabled }]}>Off hours</Text>
                )}
              </View>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default MobileTeamView;
